import React, { useEffect, useState } from "react";
import { AppProvider as PolarisProvider } from "@shopify/polaris";
import { Provider as AppBridgeProvider } from "@shopify/app-bridge-react";

import {
  BrowserRouter,
  Routes,
  Route,
  useNavigate,
  useLocation,
  Navigate,
} from "react-router-dom";
import createApp from "@shopify/app-bridge";
import { Page, Text } from "@shopify/polaris";
import AiSetup from "./Components/AiSetup";
import Dashboard from "./Components/Dashboard";
import ToneContent from "./Components/ToneContent";
import ContentSetup from "./Components/ContentSetup";
import GenericContentInput from "./Components/GenericContentInput";
import GenericProductInput from "./Components/GenericProductInput";
import ArticleSuggestions from "./Components/ArticleSuggestions";
import AllBlogList from "./Components/AllBlogList";
import { ContentProvider } from "./Provider/ContentContext";
import axios from "axios";
import EditBlogs from "./Components/EditBlogs-TipTap";
import Layout from "./Components/Layout";
import Settings from "./Components/Settings";
import "./App.css";
import RegenerateTitleCombos from "./Components/RegenerateTitleCombos";
import Subscription from "./Components/Subscription";
import AllPostTable from "./Components/AllPostTable";

const apiKey = process.env.REACT_APP_SHOPIFY_API_KEY;

const protectedRoutes = ["/", "/all-blogs-listing"];

function SubscriptionGuard({ children, shopOrigin }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [isSubscribed, setIsSubscribed] = useState(false);

  // console.log("Called via Subscription gaurd app.js");

  useEffect(() => {
    async function checkSubscription() {
      try {
        const res = await axios.get(`/get_subscription/?shop_name=${shopOrigin}`);
        setIsSubscribed(!!res.data.plan);
      } catch (error) {
        setIsSubscribed(false);
      } finally {
        setLoading(false);
      }
    }
    checkSubscription();
  }, [shopOrigin, location.pathname]); // Add location.pathname as dependency

  if (loading) {
    return <div></div>;
  }

  const path = location.pathname;

  // If user not subscribed and tries to access protected route, redirect to subscription
  if (!isSubscribed && protectedRoutes.includes(path)) {
    return <Navigate to={`/subscription?shop=${encodeURIComponent(shopOrigin)}`} replace />;
  }

  // If user tries to leave settings but is not subscribed, force redirect back to settings
  if (!isSubscribed && path !== "/subscription" && path !== "/settings") {
    return <Navigate to={`/settings?shop=${encodeURIComponent(shopOrigin)}`} replace />;
  }

  return children;
}

// Component to handle /app/ route and redirect logic
function AppRedirect() {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const shop = searchParams.get("shop");
  const host = searchParams.get("host");
  const [appBridgeError, setAppBridgeError] = useState(null);

  useEffect(() => {
    if (!shop || !host) {
      console.error("Missing shop or host parameters");
      navigate("/error", {
        replace: true,
        state: { error: "Missing shop or host parameters" },
      });
      return;
    }

    // Initialize App Bridge
    const initializeAppBridge = async () => {
      try {
        const app = createApp({
          apiKey,
          host,
          forceRedirect: true,
        });
        // console.log("AppBridge initialized:", app);
      } catch (error) {
        console.error("AppBridge initialization failed:", error);
        setAppBridgeError(error.message);
        // Fallback redirect
        window.location.assign(`/app/?shop=${shop}&host=${host}`);
      }
    };

    if (window.top === window.self) {
      // console.log("Not in iframe, redirecting to Shopify admin");
      window.location.assign(`https://${shop}/admin/apps/blogcraftai`);
    } else {
      initializeAppBridge();
    }
  }, [shop, host, navigate]);

  useEffect(() => {
    if (!shop || !host) return;

    const fetchSetupStatus = async () => {
      try {
        const tokenResponse = await axios.get(
          `/api/get-access-token/?shop=${shop}`
        );
        const { access_token } = tokenResponse.data;

        const response = await axios.get(
          `/api/check-setup-status/?shop=${shop}`
        );
        const { initial_steps_completed } = response.data;

        //console.log("Setup status:", initial_steps_completed);
        if (initial_steps_completed) {
          navigate(`/dashboard?shop=${shop}`, {
            replace: true,
            state: { shop, apiKey: access_token },
          });
        } else {
          navigate(`/settings`, {
            replace: true,
            state: { shop, apiKey: access_token },
          });
        }
      } catch (err) {
        console.error("Error checking setup status or fetching token:", err);
        // navigate(`/ai-setup?shop=${shop}`, {
        navigate(`/settings`, {
          replace: true,
          state: { shop },
        });
      }
    };

    fetchSetupStatus();
  }, [navigate, shop, host]);

  if (!shop || !host || appBridgeError) {
    return (
      <Page title="Error">
        <Text variant="bodyMd" color="critical">
          ⚠️ Error: {appBridgeError || "Missing shop or host parameters"}.
          Please launch from Shopify Admin or enable cookies.
        </Text>
      </Page>
    );
  }

  return null;
}


function App() {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const shopOrigin = searchParams.get("shop") || location.state?.shop;

  return (
    <ContentProvider>
      <PolarisProvider>
        <Layout shopOrigin={shopOrigin} apiKey={apiKey}>
          {/* <SubscriptionGuard shopOrigin={shopOrigin}> */}
            <Routes>
              <Route path="/app/" element={<AppRedirect />} />
              <Route
                path="/ai-setup"
                element={<AiSetup shopOrigin={shopOrigin} apiKey={apiKey} />}
              />
              <Route
                path="/dashboard"
                element={<Dashboard shopOrigin={shopOrigin} apiKey={apiKey} />}
              />
              <Route path="/" element={<AppRedirect />} />
              <Route
                path="/tone-content"
                element={<ToneContent shopOrigin={shopOrigin} apiKey={apiKey} />}
              />
              <Route
                path="/content-setup"
                element={<ContentSetup shopOrigin={shopOrigin} apiKey={apiKey} />}
              />
              <Route
                path="/generic-content"
                element={
                  <GenericContentInput shopOrigin={shopOrigin} apiKey={apiKey} />
                }
              />
              <Route
                path="/generic-product"
                element={
                  <GenericProductInput shopOrigin={shopOrigin} apiKey={apiKey} />
                }
              />
              <Route
                path="/article-suggestion"
                element={
                  <ArticleSuggestions shopOrigin={shopOrigin} apiKey={apiKey} />
                }
              />
              <Route
                path="/all-blogs-listing"
                element={<AllBlogList shopOrigin={shopOrigin} apiKey={apiKey} />}
              />
              <Route
                path="/edit-blogs"
                element={<EditBlogs shopOrigin={shopOrigin} apiKey={apiKey} />}
              />
              <Route
                path="/settings"
                element={<Settings shopOrigin={shopOrigin} apiKey={apiKey}/>}
              />
              <Route
                path="/subscription"
                element={<Subscription shopOrigin={shopOrigin} apiKey={apiKey}/>}
              />
              <Route path="/regenerate-title-combos" element={<RegenerateTitleCombos shopOrigin={shopOrigin} apiKey={apiKey}/>} />
              <Route path="/all-posts" element={<AllPostTable shopOrigin={shopOrigin} apiKey={apiKey}/>} />

            </Routes>
          {/* </SubscriptionGuard> */}
        </Layout>
      </PolarisProvider>
    </ContentProvider>
  );
}

export default function AppWrapper() {
  // Get shop and host from the URL
  const location = window.location;
  const searchParams = new URLSearchParams(location.search);
  const shopOrigin = searchParams.get("shop");
  const host = searchParams.get("host");

  const config = {
    apiKey: process.env.REACT_APP_SHOPIFY_API_KEY,
    host: host, // or shopOrigin if that's what you use
    forceRedirect: true,
  };

  return (
    <BrowserRouter>
      <PolarisProvider config={config}>
        <App className="App" />
      </PolarisProvider>
    </BrowserRouter>
  );
}